// Modal — base modalove okno + sub-komponenty
// Ustava sek. 53.1: pri Esc / X / overlay click ked je dirty -> DirtyConfirm dialog
// Ustava sek. 54.1: Esc = zavriet
// Max-width: min(1200px, 95vw) per zadanie

import React, { useEffect, useRef } from 'react'
import { COLORS, RADIUS, TYPO, SPACE, SHADOW, Z_INDEX, FOCUS_OUTLINE } from '../theme.js'
import { Btn, BtnAdd, BtnGhost, BtnDanger } from './Btn.jsx'

export function Modal({
  open,
  onClose,
  title = '',
  subtitle = '',
  children,
  footer = null,
  maxWidth = 1200,
  width = 'min(1200px, 95vw)',
  closeOnOverlay = true,
  closeOnEsc = true,
  isDirty = false,
  onDirtyClose,            // ked je dirty=true a user chce zavriet -> volame namiesto onClose
  style = {}
}) {
  const overlayRef = useRef(null)
  const contentRef = useRef(null)

  // Esc handler (sek. 54.1)
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (e.key === 'Escape' && closeOnEsc) {
        e.preventDefault()
        e.stopPropagation()   // 16. 9. 2026 (Kronika 511): Esc zavrie len okno — nesmie prejsť do klávesnice zoznamu pod ním (useListKeyboard počúva na window)
        if (isDirty && onDirtyClose) onDirtyClose()
        else if (onClose) onClose()
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, closeOnEsc, isDirty, onDirtyClose, onClose])

  // Focus trap basic — pri otvoreni focus na prvy interaktivny prvok
  useEffect(() => {
    if (!open || !contentRef.current) return
    const focusable = contentRef.current.querySelector(
      'input, button, select, textarea, a[href], [tabindex]:not([tabindex="-1"])'
    )
    if (focusable) focusable.focus()
  }, [open])

  if (!open) return null

  const computedMaxWidth = typeof maxWidth === 'number' ? `min(${maxWidth}px, 95vw)` : (width || maxWidth)

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'matrix-modal-title' : undefined}
      onClick={(e) => {
        if (e.target === overlayRef.current && closeOnOverlay) {
          if (isDirty && onDirtyClose) onDirtyClose()
          else if (onClose) onClose()
        }
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: SPACE.xl,
        paddingTop: '5vh',
        zIndex: Z_INDEX.modal,
        animation: 'matrix-modal-overlay-in 150ms ease-out'
      }}
    >
      <div
        ref={contentRef}
        style={{
          background: COLORS.bg,
          borderRadius: RADIUS.lg,
          boxShadow: SHADOW.modal,
          width: computedMaxWidth,
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          ...style
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || subtitle) && (
          <div style={{
            padding: `${SPACE.lg}px ${SPACE.xl}px`,
            borderBottom: `1px solid ${COLORS.line}`,
            display: 'flex',
            alignItems: 'flex-start',
            gap: SPACE.md
          }}>
            <div style={{ flex: 1 }}>
              {title && (
                <h2 id="matrix-modal-title" style={{
                  margin: 0,
                  fontSize: TYPO.sizes.lg,
                  fontWeight: TYPO.weights.bold,
                  color: COLORS.text
                }}>
                  {isDirty && <span style={{ color: COLORS.add, marginRight: 6 }}>●</span>}
                  {title}
                </h2>
              )}
              {subtitle && (
                <p style={{ margin: '4px 0 0', fontSize: TYPO.sizes.sm, color: COLORS.muted }}>{subtitle}</p>
              )}
            </div>
            <button
              onClick={() => isDirty && onDirtyClose ? onDirtyClose() : onClose && onClose()}
              aria-label="Zavriet"
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: 24,
                lineHeight: 1,
                color: COLORS.muted,
                cursor: 'pointer',
                padding: 0,
                width: 30,
                height: 30
              }}
            >×</button>
          </div>
        )}

        <div data-formular-telo style={{ flex: 1, overflow: 'auto', padding: SPACE.xl }}>
          {children}
        </div>

        {footer && (
          <div style={{
            padding: `${SPACE.md}px ${SPACE.xl}px`,
            borderTop: `1px solid ${COLORS.line}`,
            background: COLORS.bgSoft,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: SPACE.sm
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================
// <Modal.DirtyConfirm> — dialog "Ulozit / Pokracovat / Zahodit" (sek. 53.1)
// Pouzitie:
//   <Modal.DirtyConfirm
//     open={dirtyDialogOpen}
//     onSave={() => { save(); close() }}
//     onContinue={() => setDirtyDialogOpen(false)}
//     onDiscard={() => { revert(); close() }}
//   />
// ============================================================
function DirtyConfirm({ open, onSave, onContinue, onDiscard, message = 'V editácii sú neuložené zmeny.' }) {
  const [discardConfirm, setDiscardConfirm] = React.useState(false)

  // Esc = Pokracovat (najmensie riziko straty)
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onContinue && onContinue() }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onContinue])

  // Reset druheho potvrdenia ked sa dialog zatvori
  useEffect(() => {
    if (!open) setDiscardConfirm(false)
  }, [open])

  if (!open) return null

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACE.xl,
        zIndex: Z_INDEX.modal + 100
      }}
    >
      <div style={{
        background: COLORS.bg,
        borderRadius: RADIUS.lg,
        boxShadow: SHADOW.modal,
        width: 'min(480px, 95vw)',
        padding: SPACE.xl
      }}>
        <h3 style={{ margin: 0, fontSize: TYPO.sizes.lg, fontWeight: TYPO.weights.bold }}>
          ● Neuložené zmeny
        </h3>
        <p style={{ margin: `${SPACE.sm}px 0 ${SPACE.xl}px`, color: COLORS.text, fontSize: TYPO.sizes.base }}>
          {message}
        </p>

        {!discardConfirm ? (
          <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <BtnGhost onClick={() => setDiscardConfirm(true)}>Zahodiť…</BtnGhost>
            <Btn variant="ghost" onClick={onContinue}>Pokračovať v editácii</Btn>
            <BtnAdd onClick={onSave} autoFocus>Uložiť zmeny</BtnAdd>
          </div>
        ) : (
          <div>
            <p style={{ margin: `0 0 ${SPACE.md}px`, color: COLORS.remove, fontSize: TYPO.sizes.sm, fontWeight: TYPO.weights.semibold }}>
              Naozaj zahodiť? Zmeny sa nedajú vrátiť.
            </p>
            <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end' }}>
              <BtnGhost onClick={() => setDiscardConfirm(false)}>Späť</BtnGhost>
              <BtnDanger onClick={onDiscard}>Áno, zahodiť zmeny</BtnDanger>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

Modal.DirtyConfirm = DirtyConfirm

export default Modal
